import React, { useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  AccountBalanceWallet,
  Analytics,
  Group,
  TrendingUp,
  Category,
  Receipt,
  Phone,
  Email,
  LocationOn
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const NexpenxDetail: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box>
      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ backgroundColor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1, 
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src="/logo-square.png" 
              alt="Prodxpress Logo" 
              style={{ 
                height: '40px', 
                marginRight: '12px',
                maxWidth: '50px'
              }}
            />
            <img 
              src="/logo-title.png" 
              alt="PRODXPRESS" 
              style={{ 
                height: '24px', 
                maxWidth: '150px',
                objectFit: 'contain'
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: { xs: 10, md: 12 },
          pb: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #1a4d1a 0%, #2e7d32 50%, #4caf50 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ 
            color: 'white', 
            mb: 3, 
            fontWeight: 700,
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
            fontFamily: 'Prompt, sans-serif'
          }}>
            Nexpenx
          </Typography>
          <Typography variant="h4" gutterBottom sx={{ 
            color: 'white', 
            opacity: 0.9, 
            mb: 2,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
          }}>
            Smart Expense Tracking for Life's Every Moment
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ 
            color: 'white', 
            opacity: 0.8, 
            mb: 4, 
            maxWidth: '800px', 
            mx: 'auto',
            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            px: { xs: 2, sm: 0 }
          }}>
            Track, categorize, and analyze your expenses in real-time with intelligent insights. 
            From fundamentals to fun activities and future planning, take control of your financial journey.
          </Typography>
        </Container>
      </Box>

      {/* Core Features */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ 
            mb: 6, 
            color: 'text.primary',
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
          }}>
            Complete Feature Overview
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 4, 
            justifyContent: 'center',
            alignItems: 'stretch'
          }}>
            {/* Smart Categorization */}
            <Box sx={{ flex: '1 1 300px', maxWidth: { xs: '100%', sm: '45%', lg: '30%' } }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 3, md: 4 } }}>
                  <Category sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}>
                    Intelligent Categories
                  </Typography>
                  <Chip label="3-Tier System" color="primary" size="small" sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    Organize expenses with our proven 3-category framework for balanced financial living.
                  </Typography>
                  <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                    <li><strong>Fundamentals:</strong> Housing, Food, Transportation, Healthcare</li>
                    <li><strong>Fun:</strong> Entertainment, Dining Out, Hobbies, Travel</li>
                    <li><strong>Future Planning:</strong> Savings, Investments, Education, Insurance</li>
                    <li>Custom subcategories under each main category</li>
                    <li>Smart auto-categorization based on spending patterns</li>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Real-time Tracking */}
            <Box sx={{ flex: '1 1 300px', maxWidth: { xs: '100%', sm: '45%', lg: '30%' } }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 3, md: 4 } }}>
                  <Receipt sx={{ fontSize: 48, color: '#1a4d1a', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}>
                    Instant Expense Logging
                  </Typography>
                  <Chip label="On-the-Go" size="small" sx={{ mb: 2, backgroundColor: '#1a4d1a', color: 'white' }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    Capture expenses the moment they happen with lightning-fast entry methods.
                  </Typography>
                  <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                    <li>Scan QR code directly and make payment with any Payments Apps</li>
                    <li>Scan QR code from an image and make payment</li>
                    <li>Quick-add buttons for common expenses</li>
                    <li>Offline mode with sync when connected</li>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Analytics & Reports */}
            <Box sx={{ flex: '1 1 300px', maxWidth: { xs: '100%', sm: '45%', lg: '30%' } }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 3, md: 4 } }}>
                  <Analytics sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}>
                    Visual Analytics
                  </Typography>
                  <Chip label="Data-Driven" color="primary" size="small" sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    Transform your spending data into actionable insights with beautiful visualizations.
                  </Typography>
                  <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                    <li>Interactive pie charts and bar graphs</li>
                    <li>Spending trends and pattern analysis</li>
                    <li>Category-wise budget vs actual comparisons</li>
                    <li>Monthly, weekly, and custom duration breakdowns</li>
                    <li>Goal tracking with progress indicators</li>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Social Splitting */}
            <Box sx={{ flex: '1 1 300px', maxWidth: { xs: '100%', sm: '45%', lg: '30%' } }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 3, md: 4 } }}>
                  <Group sx={{ fontSize: 48, color: '#1a4d1a', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}>
                    Smart Expense Splitting
                  </Typography>
                  <Chip label="Social" size="small" sx={{ mb: 2, backgroundColor: '#1a4d1a', color: 'white' }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    Split bills effortlessly with friends and track shared expenses automatically.
                  </Typography>
                  <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                    <li>Equal or custom split amounts</li>
                    <li>Send split requests via app</li>
                    <li>Track who owes what with payment status</li>
                    <li>Group expense management for trips/events</li>
                    <li>Settlement reminders and notifications</li>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Consolidated View */}
            <Box sx={{ flex: '1 1 300px', maxWidth: { xs: '100%', sm: '45%', lg: '30%' } }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 3, md: 4 } }}>
                  <AccountBalanceWallet sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}>
                    Consolidated Dashboard
                  </Typography>
                  <Chip label="Unified View" color="primary" size="small" sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    See your complete financial picture in one comprehensive, easy-to-understand dashboard.
                  </Typography>
                  <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                    <li>All personal expenses in chronological order</li>
                    <li>Separate view for shared/split expenses</li>
                    <li>Combined summary with net personal spending</li>
                    <li>Track your family members expenses together</li>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Goal Setting */}
            <Box sx={{ flex: '1 1 300px', maxWidth: { xs: '100%', sm: '45%', lg: '30%' } }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 3, md: 4 } }}>
                  <TrendingUp sx={{ fontSize: 48, color: '#1a4d1a', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}>
                    Financial Goal Tracking
                  </Typography>
                  <Chip label="Achievement" size="small" sx={{ mb: 2, backgroundColor: '#1a4d1a', color: 'white' }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                    Set, track, and achieve your financial goals with intelligent progress monitoring.
                  </Typography>
                  <Box component="ul" sx={{ color: 'text.secondary', pl: 2, fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                    <li>Category-wise spending budgets and limits</li>
                    <li>Overall and Category-wise spending summary</li>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ 
            mb: 6, 
            color: 'text.primary',
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
          }}>
            Why Choose Nexpenx?
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 4, 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Box sx={{ flex: '1 1 250px', textAlign: 'center', p: 3 }}>
              <Typography variant="h1" sx={{ 
                color: 'primary.main', 
                fontWeight: 900, 
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' }
              }}>
                5s
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Average Log Time
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Log any expense in under 5 seconds with our optimized interface
              </Typography>
            </Box>
            
            <Box sx={{ flex: '1 1 250px', textAlign: 'center', p: 3 }}>
              <Typography variant="h1" sx={{ 
                color: '#1a4d1a', 
                fontWeight: 900, 
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' }
              }}>
                94%
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Budget Accuracy
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Users achieve 94% accuracy in staying within their planned budgets
              </Typography>
            </Box>
            
            <Box sx={{ flex: '1 1 250px', textAlign: 'center', p: 3 }}>
              <Typography variant="h1" sx={{ 
                color: 'primary.main', 
                fontWeight: 900, 
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' }
              }}>
                $840
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Average Monthly Savings
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Users save an average of $840 per month through better expense awareness
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom sx={{ 
            color: 'white', 
            mb: 3,
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
          }}>
            Ready to Take Control of Your Finances?
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'white', 
            opacity: 0.9, 
            mb: 4,
            px: { xs: 2, sm: 0 }
          }}>
            Join thousands who've transformed their financial habits with Nexpenx
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large" 
              sx={{ 
                backgroundColor: 'white', 
                color: 'primary.main', 
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              Download Now
            </Button>
            <Button 
              variant="outlined" 
              size="large" 
              sx={{ 
                borderColor: 'white', 
                color: 'white', 
                '&:hover': { borderColor: '#f5f5f5', backgroundColor: 'rgba(255,255,255,0.1)' } 
              }}
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 6, backgroundColor: '#1E293B', color: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between' }}>
            <Box sx={{ flex: '1 1 300px', maxWidth: '400px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 700, mb: 0 }}>
                  Prodxpress
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 3 }}>
                Accelerating innovation through technology solutions that streamline operations, 
                connect communities, and drive progress across industries.
              </Typography>
            </Box>
            
            <Box sx={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Products
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/products/tracsess')}
                >
                  Tracsess
                </Button>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/products/nexpenx')}
                >
                  Nexpenx
                </Button>
                <Button sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}>
                  More Coming Soon
                </Button>
              </Box>
            </Box>

            <Box sx={{ flex: '1 1 200px', maxWidth: '300px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Support
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/support/nexpenx')}
                >
                  Account Help & Support
                </Button>
                <Button 
                  sx={{ justifyContent: 'flex-start', color: '#CBD5E1', textTransform: 'none' }}
                  onClick={() => navigate('/')}
                >
                  Back to Home
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ flex: '1 1 250px', maxWidth: '350px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                Contact Info
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    contact@prodxpress.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    +91 81222 96635
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                    Chennai, India
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #475569', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
              © 2024 Prodxpress. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default NexpenxDetail; 